import React from "react";
import cn from "./cn";

/**
 * DropZone — the dashed target that opens a file picker.
 *
 * A `<button>`, so Enter and Space work and the focus ring comes for free; the
 * drag handlers are ordinary props, because a zone that cannot be dropped on
 * (a picker that only clicks) is the same control without them.
 *
 * It owns the surface and the states. What it CONTAINS — an icon, a prompt, a
 * hint — is `children`.
 *
 * Props:
 *   size   — "sm" | "md" (default) | "lg"
 *   state  — "idle" (default) | "active" (dragged over) | "filled" (has a file)
 *
 * `enabled:hover:` rather than a conditionally-built string: the hover treatment
 * has to be suppressed while the zone is disabled, and on a real <button> that
 * is a variant rather than a branch.
 */
const DROP_ZONE_BASE_CLASSES =
  // `tw-group` so a child can tint with the zone's own hover — an icon that
  // reacts to the target it sits in, without the page tracking hover state.
  "tw-group tw-flex tw-w-full tw-items-center tw-justify-center tw-border-dashed " +
  "tw-transition-colors tw-focus-ring " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-50 " +
  "enabled:tw-cursor-pointer enabled:hover:tw-border-primary enabled:hover:tw-bg-surface-hover";

// `md` is the standalone upload box; `sm` is the compact row that sits inside a
// form field, where a six-line target would dominate the fields around it; `lg`
// is the whole-page importer, where the target IS the screen.
const DROP_ZONE_SIZE_CLASSES = {
  sm: "tw-flex-row tw-gap-2 tw-rounded-sm tw-border tw-px-3 tw-py-[18px]",
  md: "tw-flex-col tw-gap-1.5 tw-rounded-md tw-border-2 tw-px-4 tw-py-6",
  lg: "tw-flex-col tw-gap-1.5 tw-rounded tw-border-2 tw-px-4 tw-py-10",
};

// Three states, not a boolean: a zone holding a file reads differently from one
// being dragged over, and both differ from empty. `filled` goes solid, because a
// dashed edge says "drop here" and this one is done.
const DROP_ZONE_STATE_CLASSES = {
  idle: "tw-border-line tw-bg-surface",
  active: "tw-border-primary tw-bg-surface-hover",
  filled: "tw-border-solid tw-border-success tw-bg-success/[0.06]",
};

const DROP_ZONE_SIZES = new Set(["sm", "md", "lg"]);
const warnedSizes = new Set();
const warnedStates = new Set();

const resolveSize = (size) => {
  if (DROP_ZONE_SIZES.has(size)) return size;
  if (process.env.NODE_ENV !== "production" && !warnedSizes.has(size)) {
    warnedSizes.add(size);
    // eslint-disable-next-line no-console
    console.warn(
      `[DropZone] unknown size "${size}" — falling back to "md". Valid sizes: sm, md, lg.`
    );
  }
  return "md";
};

const resolveState = (state) => {
  if (Object.hasOwn(DROP_ZONE_STATE_CLASSES, state)) return state;
  if (process.env.NODE_ENV !== "production" && !warnedStates.has(state)) {
    warnedStates.add(state);
    // eslint-disable-next-line no-console
    console.warn(
      `[DropZone] unknown state "${state}" — falling back to "idle". ` +
        `Valid states: ${Object.keys(DROP_ZONE_STATE_CLASSES).join(", ")}.`
    );
  }
  return "idle";
};

const DropZone = React.forwardRef(
  ({ state = "idle", size = "md", className = "", children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        DROP_ZONE_BASE_CLASSES,
        DROP_ZONE_SIZE_CLASSES[resolveSize(size)],
        DROP_ZONE_STATE_CLASSES[resolveState(state)],
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
DropZone.displayName = "DropZone";

export default DropZone;
